import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useConnect, useAccount, useDisconnect } from 'wagmi';
import { injected } from 'wagmi/connectors';

// Only log in development
const isDev = process.env.NODE_ENV !== 'production';

interface CustomWalletModalProps {
  visible: boolean;
  onClose: () => void;
}

type WalletType = 'metamask' | 'phantom';

export const CustomWalletModal: React.FC<CustomWalletModalProps> = ({ visible, onClose }) => {
  const { connect } = useConnect();
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  
  const [connecting, setConnecting] = React.useState(false);
  const [connectingWallet, setConnectingWallet] = React.useState<WalletType | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Clear error when modal closes
  React.useEffect(() => {
    if (!visible) {
      setError(null);
      setConnecting(false);
      setConnectingWallet(null);
    }
  }, [visible]);

  // Close modal when connected
  React.useEffect(() => {
    if (isConnected && visible) {
      const timer = setTimeout(() => {
        onClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isConnected, visible, onClose]);

  const handleConnect = async (walletType: WalletType) => {
    try {
      setError(null);
      setConnecting(true);
      setConnectingWallet(walletType);
      
      const target = walletType === 'metamask' ? 'metaMask' : 'phantom';
      await connect({ connector: injected({ target }) });
      
      isDev && console.log(`${walletType} wallet connected`);
    } catch (err: any) {
      console.error('Wallet connection error:', err);
      if (!err.message?.includes('User rejected')) {
        setError(err.message || 'Failed to connect wallet');
      }
    } finally {
      setConnecting(false);
      setConnectingWallet(null);
    }
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="wallet-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        key="wallet-modal-content"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 w-[420px] max-w-[90vw] h-screen bg-gradient-to-b from-slate-900 to-slate-800 border-l border-blue-500/20 shadow-2xl z-[10000] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <div>
            <h2 className="text-xl font-semibold text-white">
              Connect Wallet
            </h2>
            <p className="text-xs text-gray-400 mt-1">
              {isConnected 
                ? 'Connected ✓' 
                : 'Choose your preferred wallet'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
          >
            <svg className="w-4 h-4 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
            >
              <p className="text-sm text-red-400 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </p>
            </motion.div>
          )}

          {/* Connected Status */}
          {isConnected && address && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-6 p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-green-400 font-semibold">Wallet Connected!</p>
                  <p className="text-xs text-gray-400 font-mono">
                    {address.slice(0, 10)}...{address.slice(-8)}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => {
                  disconnect();
                  onClose();
                }}
                className="w-full text-xs px-3 py-2.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-red-500/20 font-semibold"
              >
                Disconnect
              </button>
            </motion.div>
          )}

          {/* Wallet Options */}
          {!isConnected && (
            <div className="space-y-3">
              {/* MetaMask */}
              <motion.button
                onClick={() => handleConnect('metamask')}
                disabled={connecting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full p-5 rounded-xl bg-gradient-to-r from-orange-500/20 to-amber-500/20 hover:from-orange-500/30 hover:to-amber-500/30 border border-orange-500/30 hover:border-orange-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center justify-center gap-4">
                  <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg" 
                      alt="MetaMask"
                      className="w-10 h-10"
                    />
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-white font-semibold text-lg">MetaMask</p>
                    <p className="text-gray-400 text-sm">Popular browser wallet</p>
                  </div>
                  {connecting && connectingWallet === 'metamask' ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-orange-400 border-t-transparent" />
                  ) : (
                    <svg className="w-6 h-6 text-orange-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </motion.button>

              {/* Phantom */}
              <motion.button
                onClick={() => handleConnect('phantom')}
                disabled={connecting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full p-5 rounded-xl bg-gradient-to-r from-purple-500/20 to-indigo-500/20 hover:from-purple-500/30 hover:to-indigo-500/30 border border-purple-500/30 hover:border-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-center justify-center gap-4">
                  <div className="w-14 h-14 bg-[#AB9FF2]/20 rounded-xl flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="128" height="128" rx="26.8387" fill="url(#phantom-gradient)"/>
                      <path d="M110.584 64.9142H99.142C99.142 42.0562 80.5878 23.5765 57.6373 23.5765C35.0048 23.5765 16.6384 41.5471 16.0918 63.9139C15.5318 86.8286 35.7604 106.424 58.7105 106.424H62.9911C83.4468 106.424 110.584 86.6592 110.584 64.9142Z" fill="url(#phantom-gradient2)"/>
                      <path d="M77.5879 62.7012C77.5879 66.7615 74.4114 70.0569 70.4992 70.0569C66.587 70.0569 63.4106 66.7615 63.4106 62.7012C63.4106 58.6408 66.587 55.3455 70.4992 55.3455C74.4114 55.3455 77.5879 58.6408 77.5879 62.7012Z" fill="#534AB8"/>
                      <path d="M56.5039 62.7012C56.5039 66.7615 53.3274 70.0569 49.4152 70.0569C45.503 70.0569 42.3265 66.7615 42.3265 62.7012C42.3265 58.6408 45.503 55.3455 49.4152 55.3455C53.3274 55.3455 56.5039 58.6408 56.5039 62.7012Z" fill="#534AB8"/>
                      <defs>
                        <linearGradient id="phantom-gradient" x1="64" y1="0" x2="64" y2="128" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#534BB1"/>
                          <stop offset="1" stopColor="#551BF9"/>
                        </linearGradient>
                        <linearGradient id="phantom-gradient2" x1="63.338" y1="23.5765" x2="63.338" y2="106.424" gradientUnits="userSpaceOnUse">
                          <stop stopColor="white"/>
                          <stop offset="1" stopColor="#EBEBEB"/>
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-white font-semibold text-lg">Phantom</p>
                    <p className="text-gray-400 text-sm">Multi-chain wallet</p>
                  </div>
                  {connecting && connectingWallet === 'phantom' ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-400 border-t-transparent" />
                  ) : (
                    <svg className="w-6 h-6 text-purple-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </motion.button>
            </div>
          )}

          {/* Wallet Info */}
          {!isConnected && (
            <div className="mt-6 p-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg" 
                    alt="MetaMask"
                    className="w-6 h-6"
                  />
                  <svg width="24" height="24" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="128" height="128" rx="26.8387" fill="url(#phantom-small)"/>
                    <path d="M110.584 64.9142H99.142C99.142 42.0562 80.5878 23.5765 57.6373 23.5765C35.0048 23.5765 16.6384 41.5471 16.0918 63.9139C15.5318 86.8286 35.7604 106.424 58.7105 106.424H62.9911C83.4468 106.424 110.584 86.6592 110.584 64.9142Z" fill="white"/>
                    <circle cx="70.5" cy="62.7" r="7" fill="#534AB8"/>
                    <circle cx="49.4" cy="62.7" r="7" fill="#534AB8"/>
                    <defs>
                      <linearGradient id="phantom-small" x1="64" y1="0" x2="64" y2="128" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#534BB1"/>
                        <stop offset="1" stopColor="#551BF9"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <span className="text-sm text-gray-400">
                  Secure Web3 wallets for EVM chains
                </span>
              </div>
            </div>
          )}

          {/* Terms */}
          <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
            <p className="text-xs text-gray-400 leading-relaxed">
              By connecting, you agree to Eredium's Terms of Service and acknowledge that you have read the protocol disclaimer.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/5">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <span>Powered by</span>
            <span className="text-blue-400 font-semibold">Wagmi</span>
            <span>🔐</span>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
